/**
 * Enum values copied verbatim from the monday.com 2026-10 introspection schema
 * (docs/09-monday-api-reference-notes.md §A.5, §A.6). Never edit by hand from memory:
 * re-derive from `get_schema?version=2026-10`.
 */
import { z } from 'zod';
/** `ColumnType` — 43 values, identical in 2026-07 and 2026-10. */
export const COLUMN_TYPES = [
    'auto_number',
    'board_relation',
    'button',
    'checkbox',
    'color_picker',
    'country',
    'creation_log',
    'date',
    'dependency',
    'direct_doc',
    'doc',
    'dropdown',
    'email',
    'file',
    'formula',
    'group',
    'hour',
    'integration',
    'item_assignees',
    'item_id',
    'last_updated',
    'link',
    'location',
    'long_text',
    'mirror',
    'name',
    'numbers',
    'people',
    'person',
    'phone',
    'progress',
    'rating',
    'status',
    'subtasks',
    'tags',
    'team',
    'text',
    'time_tracking',
    'timeline',
    'unsupported',
    'vote',
    'week',
    'world_clock',
];
export const columnTypeSchema = z.enum(COLUMN_TYPES);
/** `StatusColumnColors` — 40 values. This is the monday status-label palette. */
export const STATUS_COLORS = [
    'working_orange',
    'done_green',
    'stuck_red',
    'dark_blue',
    'purple',
    'explosive',
    'grass_green',
    'bright_blue',
    'saladish',
    'egg_yolk',
    'blackish',
    'dark_red',
    'sofia_pink',
    'lipstick',
    'dark_purple',
    'bright_green',
    'chili_blue',
    'american_gray',
    'brown',
    'dark_orange',
    'sunset',
    'bubble',
    'peach',
    'berry',
    'winter',
    'river',
    'navy',
    'aquamarine',
    'indigo',
    'dark_indigo',
    'pecan',
    'lavender',
    'royal',
    'steel',
    'orchid',
    'lilac',
    'tan',
    'sky',
    'coffee',
    'teal',
];
export const statusColorSchema = z.enum(STATUS_COLORS);
/** `BoardKind` (lowercase form used on reads). */
export const BOARD_KINDS = ['private', 'public', 'share'];
export const boardKindSchema = z.enum(BOARD_KINDS);
/** `State` — used by boards, items, groups, columns. `all` is a query filter only. */
export const STATES = ['active', 'archived', 'deleted'];
export const stateSchema = z.enum(STATES);
/** `WorkspaceKind`. */
export const WORKSPACE_KINDS = ['closed', 'open', 'template'];
export const workspaceKindSchema = z.enum(WORKSPACE_KINDS);
/** `ViewKind` — accepted by `create_view` / `update_view`. */
export const VIEW_KINDS = ['DASHBOARD', 'TABLE', 'FORM', 'APP'];
export const viewKindSchema = z.enum(VIEW_KINDS);
/** `ExternalWidget` — dashboard widget kinds. */
export const EXTERNAL_WIDGETS = [
    'CHART',
    'NUMBER',
    'BATTERY',
    'CALENDAR',
    'GANTT',
    'APP_FEATURE',
    'LISTVIEW',
];
export const externalWidgetSchema = z.enum(EXTERNAL_WIDGETS);
/** `WebhookEventType` — all 21 values. */
export const WEBHOOK_EVENT_TYPES = [
    'change_column_value',
    'change_name',
    'change_specific_column_value',
    'change_status_column_value',
    'change_subitem_column_value',
    'change_subitem_name',
    'create_column',
    'create_item',
    'create_subitem',
    'create_subitem_update',
    'create_update',
    'delete_update',
    'edit_update',
    'item_archived',
    'item_deleted',
    'item_moved_to_any_group',
    'item_moved_to_specific_group',
    'item_restored',
    'move_subitem',
    'subitem_archived',
    'subitem_deleted',
];
export const webhookEventTypeSchema = z.enum(WEBHOOK_EVENT_TYPES);
/** `ItemsQueryRuleOperator` — the filter operators a column type may advertise. */
export const FILTER_OPERATORS = [
    'any_of',
    'not_any_of',
    'is_empty',
    'is_not_empty',
    'within_the_last',
    'within_the_next',
    'greater_than',
    'greater_than_or_equals',
    'lower_than',
    'lower_than_or_equal',
    'between',
    'starts_with',
    'ends_with',
    'contains_text',
    'contains_terms',
    'not_contains_text',
];
export const filterOperatorSchema = z.enum(FILTER_OPERATORS);
/** `ItemsQueryOperator`. */
export const FILTER_COMBINATORS = ['and', 'or'];
/**
 * Group-summary aggregations a column type can offer (monday's column footer menu).
 * Not an API enum — ours, but named after monday's UI labels.
 */
export const COLUMN_AGGREGATIONS = [
    'none',
    'sum',
    'avg',
    'median',
    'min',
    'max',
    'count',
    'count_filled',
    'count_empty',
    'earliest',
    'latest',
    'range',
    'battery',
    /** people/person/team footers: distinct assignees with counts (M1-B5 §5.3, doc 05 B4d). */
    'avatars',
];
/**
 * Account roles (doc 04 · Permissions) live in `roles.ts` since round 9f, because there are now
 * two lists: monday's four and ours (monday's four + `superadmin`). Re-exported here so every
 * existing `from '@elizmonday/shared'` import keeps resolving.
 */
export { ACCOUNT_ROLES, ACCOUNT_ROLE_LABELS, MONDAY_ACCOUNT_ROLES, SUPERADMIN_ROLE, accountRoleSchema, isAdminRole, isSuperAdminRole, mondayAccountRoleSchema, userKindForRole, } from './roles.js';
/**
 * `Board.permissions: String!` — monday's **verbatim** values (M0-F8 §4.1). The field is
 * free-form in monday's schema and the four values below are the ones this account uses
 * (`everyone` × 406, `owners` × 42, `collaborators` × 6, `assignee` × 2, discovery/first-look).
 *
 * UI meaning (ASSUMED, OQ-F8-1): `everyone` = edit everything, `collaborators` = edit content,
 * `assignee` = edit rows assigned to them, `owners` = view only.
 */
export const BOARD_PERMISSIONS = ['everyone', 'collaborators', 'assignee', 'owners'];
/** Permissive on read: any string monday ever returns must round-trip verbatim. */
export const boardPermissionsSchema = z.string().max(64);
/** Strict on write: only the four values we know how to evaluate may be set through our API. */
export const boardPermissionsWriteSchema = z.enum(BOARD_PERMISSIONS);
/**
 * Any unrecognised `boards.permissions` string is evaluated as the **most restrictive** level
 * (`owners`), never rejected — the stored value stays verbatim (M0-F8 §4.1, acceptance A2).
 */
export function normalizeBoardPermissions(value) {
    return BOARD_PERMISSIONS.includes(value ?? '')
        ? value
        : 'owners';
}
/** `UserStatus` — lowercase in the database, uppercase on the `/v2` wire (M0-F3 §4.1). */
export const USER_STATUSES = ['active', 'inactive', 'pending'];
export const userStatusSchema = z.enum(USER_STATUSES);
/** `InvitationMethod` (lowercase form; `/v2` upper-cases it). */
export const INVITATION_METHODS = [
    'unknown',
    'user',
    'auth_domain',
    'first_user',
    'api_user_creation',
    'scim',
    'sso',
];
export const invitationMethodSchema = z.enum(INVITATION_METHODS);
/** Per-user theme (monday's avatar menu → "Change theme", discovery/admin-settings §4). */
export const USER_THEMES = ['light', 'dark', 'night'];
export const userThemeSchema = z.enum(USER_THEMES);
/** Per-user overrides of the account default (ASSUMED, OQ-F3-4). */
export const WEEK_STARTS = ['sunday', 'monday'];
export const weekStartSchema = z.enum(WEEK_STARTS);
export const DATE_FORMATS = ['MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD'];
export const dateFormatSchema = z.enum(DATE_FORMATS);
/** Which `IdentityProvider` minted a session / identity row (M0-F3 §5.1). */
export const IDENTITY_PROVIDER_KINDS = ['password', 'oidc'];
/** Personal tokens are minted by a human; service tokens by `create_service_user` (M9 §4). */
export const API_TOKEN_KINDS = ['personal', 'service'];
/** Column restriction kinds — an allow-list membership, not a level ladder (M0-F8 §4.3). */
export const COLUMN_PERMISSION_KINDS = ['view', 'edit'];
/** Board subscriber roles (doc 04 · `board_subscribers`). */
export const BOARD_SUBSCRIBER_ROLES = ['owner', 'subscriber'];
/** Public API versions we serve on `/v2` (doc 04 · Public API). */
export const API_VERSIONS = ['2026-10', '2026-07'];
export const apiVersionSchema = z.enum(API_VERSIONS);
export const DEFAULT_API_VERSION = '2026-10';
//# sourceMappingURL=enums.js.map